import PropTypes from 'prop-types';
import React from 'react';
import Flexbox from 'flexbox-react';
import { css } from 'emotion';
import { Button, Popover } from 'antd';
import InfoBoxLabel from 'components/core/InfoBoxLabel';
import RIESecondsInput from 'components/core/editable/RIESecondsInput';
import RIEToggle from 'components/core/editable/RIEToggle';
import ProcessValidator from 'utils/ProcessValidator';
import ProcessUtils from 'utils/ProcessUtils';
import ComponentUtils from 'utils/ComponentUtils';
import Constants from 'utils/Constants';
import _ from 'lodash';
import NavigationUtils from 'utils/NavigationUtils';
import AbstractForm from 'components/page/process/AbstractForm';
import ProcessNameField from 'components/core/fields/ProcessNameField';
import ProcessEmailField from 'components/core/fields/ProcessEmailField';
import ProcessDescriptionField from 'components/core/fields/ProcessDescriptionField';
import ProcessRunLocationsField from 'components/core/fields/ProcessRunLocationsField';
import ProcessCodeUrlField from 'components/core/fields/ProcessCodeUrlField';
import ProcessDeprecatedField from 'components/core/fields/ProcessDeprecatedField';
import InfoMessages from 'components/core/help/InfoMessages';
import { popoverErrorStyle } from 'styles/base';

/**
 * The form that renders the general process details
 */
export default class ProcessForm extends AbstractForm {

    static propTypes = {
        id: PropTypes.string,
        process: PropTypes.object,
        revisionNum: PropTypes.string,
        processErrors: PropTypes.object,
        dispatch: PropTypes.func,
        hasWritePermissions: PropTypes.bool,
        siteFacilities: PropTypes.array,
        dods: PropTypes.array
    };

    onProcessTypeToggleClicked = (fieldPath, value) => {
        const editor = ProcessUtils.getEditor(this.props.process);
        if (value === true) {
            editor.process.type = Constants.PROCESS_TYPE_VAP;

            // Whenever you switch to VAP, then you must add variable retrieval (i.e., ADI)
            editor.addVariableRetrieval();

            // If there is no value set, then default to 1 day for processing_interval
            let pi = editor.process.processing_interval;
            if (!pi) {
                editor.process.processing_interval = '86400';
            }

        } else {
            editor.process.type = Constants.PROCESS_TYPE_INGEST;
        }
        editor.commit();
    };

    onVariableRetrievalToggleClicked = (fieldPath, value) => {
        const editor = ProcessUtils.getEditor(this.props.process);
        if (value === true) {
            editor.addVariableRetrieval();
        } else {
            editor.removeVariableRetrieval();
        }
        editor.commit();
    };

    getInputDatastreamClassNames() {
        const process = this.props.process;

        const inputDatastreamClassNames = process.input_datastreams.reduce((accumulator, currentValue) => {
            const inputDatasets = _.get(process, 'variable_retrieval.input_datasets');
            if (inputDatasets) {
                Object.keys(inputDatasets).forEach((datasetName) => {
                    const dataset = inputDatasets[datasetName];
                    dataset.rules.forEach((rule) => {
                        if (rule.datastream_name) {
                            accumulator[rule.datastream_name] = 'badged-tag orange';
                        }
                    });
                });
            }

            return accumulator;
        }, {});
        return inputDatastreamClassNames;
    }

    getDodsAsOptions = () => {
        return this.props.dods.map((v) => {
            return { value: v, label: v };
        });
    };

    onDatastreamsNavClick = (selectedItems, lastSelectedItem, selectedId) => {
        NavigationUtils.gotoOutputDatastream(this.props.id, this.props.revisionNum, selectedId, false);
    };

    onDatastreamsUpdated = (fieldPath, value) => {
        const newValues = value.map((valueObj) => {
            return valueObj.value;
        });

        // First see if any datastreams have been deleted.  If they have been deleted, then we need to pop up a
        // confirmation dialog.
        const oldValues = this.props.process.output_datastreams;
        const itemsToDelete = {};

        oldValues.forEach((datastreamName) => {
            if (!_.includes(newValues, datastreamName)) {
                itemsToDelete[datastreamName] = true;
            }
        });

        if (_.isEmpty(itemsToDelete)) {
            this.onFieldUpdated(fieldPath, newValues);

        } else {
            ComponentUtils.openDeleteEntitiesModal(this.props.process, 'output_datastreams', itemsToDelete,
                new ProcessValidator(this.props.process).canOutputDatastreamsBeDeleted);
        }
    };

    renderProcessingInterval(rowMarginBottom) {
        const process = this.props.process;
        if (process.variable_retrieval) {
            return (
                <Flexbox flexDirection="row" marginBottom={rowMarginBottom} alignItems="flex-start">
                    <InfoBoxLabel className="nowrap" label="Processing Interval" message={InfoMessages.processingInterval}/>
                    <RIESecondsInput
                        value={process.processing_interval}
                        change={this.onFieldUpdated}
                        fieldPath={['processing_interval']}
                        isDisabled={!this.props.hasWritePermissions}
                        isFullWidth={false}
                        processId={this.props.process.name}
                        processErrors={this.props.processErrors}
                    />
                </Flexbox>
            );
        }
        return '';
    }

    render() {
        const process = this.props.process;
        const rowMarginBottom = '16px';

        const isVap = process.type === 'VAP' ? true : false;

        let wizardModeButton = (
            <Button
                type="primary"
                disabled={!ProcessUtils.canOpenInWizardMode(this.props.process)}
                onClick={NavigationUtils.gotoWizardModeFunc(this.props.processId)}
            >
                <span className={`fa fa-lg fa-magic ${css`display:inline-block; padding-right:7px;`}`} />Switch to Wizard Mode
            </Button>
        );
        if (!ProcessUtils.canOpenInWizardMode(this.props.process)) {
            wizardModeButton = (
                <Popover
                    content={ProcessUtils.getWizardModeDisabledMessage(this.props.process)}
                    placement="top"
                    overlayClassName={popoverErrorStyle}
                >
                    {wizardModeButton}
                </Popover>
            );
        }

        return (
            <Flexbox className="process-form" flexDirection="column" maxWidth="1000px" flexGrow={1}>

                {/* Name */}
                <ProcessNameField
                    className = {css`margin-bottom: ${rowMarginBottom};`}
                    process = {process}
                    processErrors = {this.props.processErrors}
                    hasWritePermissions = {this.props.hasWritePermissions}
                />

                {/* Type */}
                <Flexbox flexDirection="row" marginBottom={rowMarginBottom} alignItems="flex-start">
                    <InfoBoxLabel label="Type" width="80px" message={InfoMessages.processType} alignTop/>
                    <RIEToggle
                        value={isVap}
                        textFalse="Ingest"
                        textTrue="VAP"
                        fieldPath={['type']}
                        change={this.onProcessTypeToggleClicked}
                        isDisabled={!this.props.hasWritePermissions}
                        className="vertical-layout"
                        radioGroupName="typeRadio"
                        processId={this.props.process.name}
                    />
                </Flexbox>

                {/* Subtype */}
                <Flexbox flexDirection="row" marginBottom={rowMarginBottom} alignItems="flex-start">
                    <InfoBoxLabel label="Subtype" width="80px" message={InfoMessages.processSubtype} alignTop/>
                    <RIEToggle
                        value={process.variable_retrieval ? true : false}
                        textFalse="Basic"
                        textTrue="ADI"
                        fieldPath={['variable_retrieval']}
                        change={this.onVariableRetrievalToggleClicked}
                        isDisabled={!this.props.hasWritePermissions}
                        className="vertical-layout"
                        radioGroupName="subtypeRadio"
                        processId={this.props.process.name}
                    />
                </Flexbox>

                {/* Description */}
                <ProcessDescriptionField
                    className={css`margin-bottom: ${rowMarginBottom};`}
                    process = {process}
                    processErrors = {this.props.processErrors}
                    hasWritePermissions = {this.props.hasWritePermissions}
                />

                {/* Code URL */}
                <ProcessCodeUrlField
                    className={css`margin-bottom: ${rowMarginBottom};`}
                    process={process}
                    processErrors={this.props.processErrors}
                    hasWritePermissions={this.props.hasWritePermissions}
                />

                {/* Processing Interval */}
                { this.renderProcessingInterval(rowMarginBottom) }

                {/* Run Locations */}
                <ProcessRunLocationsField
                    className={css`margin-bottom: ${rowMarginBottom};`}
                    process = {process}
                    processErrors = {this.props.processErrors}
                    hasWritePermissions = {this.props.hasWritePermissions}
                    siteFacilities = {this.props.siteFacilities}
                />

                {/* Notifications */}
                <ProcessEmailField
                    className={css`margin-bottom: ${rowMarginBottom};`}
                    process = {process}
                    processErrors = {this.props.processErrors}
                    hasWritePermissions = {this.props.hasWritePermissions}
                />

                <ProcessDeprecatedField
                    process = {process}
                    processErrors = {this.props.processErrors}
                    hasWritePermissions = {this.props.hasWritePermissions}
                />

                <Flexbox className={css`margin-top:24px;`}>
                    {wizardModeButton}
                </Flexbox>
            </Flexbox>
        );
    }
}



// WEBPACK FOOTER //
// ./src/components/page/process/ProcessForm.js